import {
  Button,
  Checkbox,
  Group,
  Modal,
  TextInput,
  Text,
  rem,
  Flex,
  Box,
  Divider,
  Center,
  Pagination,
  CheckIcon,
  Textarea,
  Select,
  MultiSelect,
} from '@mantine/core';
import {LabelWithTooltip} from '@/components/Text/LabelWithTooltip';
import { useEffect, useState } from 'react';
import { FaMagnifyingGlass } from 'react-icons/fa6';
import { FaRegCalendarAlt } from 'react-icons/fa';
import useSWR, { mutate } from 'swr';
import {
  deleteFetcher,  getFetcher,
  postFetcher,
  putFetcher,
} from '@/api/fetchers';
import { type Task } from '@/schemas/API/GET/client-plan/task/response';
import { type TaskGroup } from '@/schemas/API/GET/client-plan/taskgroup/response';
import { type Suggestions } from '@/schemas/API/GET/client-plan/suggestions/response';
import dayjs from 'dayjs';
import { dateFormatUI } from '@/index';
import { type TaskAssignments } from '@/schemas/API/GET/client-plan/task/task-assignments/response';
import { notifications } from '@mantine/notifications';
import { displayError } from '@/utils/error/errorHandler';
import {
  type TaskRequest,
  defaultTaskRequestSchema,
} from '@/schemas/API/POST/client-plan/task/request';
import { MappingToComboBoxItem } from '@/schemas/converters/mappingShemasConverter';
import { type Mapping } from '@/schemas/API/GET/mapping/response';
import { type Tags } from '@/schemas/API/GET/tag/response';
import { DateInput } from '@mantine/dates';
import PlanningActionHandler from '@/pages/Planning/Helpers/PlanningActionHandler';
import TimeInput from '@/components/Input/TimeInput/TimeInput';

interface AssignmentsModalProps {
  clientPlanId: number;
  taskId: number;
  opened: boolean;
  close: () => void;
}

interface GroupTask {
  taskId: number;
  taskDate: string;
  assign: number[];
}

export const AssignmentsModal: React.FC<AssignmentsModalProps> = props => {
  const [page, setPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [form, setForm] = useState<TaskRequest>(defaultTaskRequestSchema);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [showOccupied, setShowOccupied] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const {
    deleteTaskGroup,
    deleteTask,
    cancelAllEmployeeGroupAssignments,
    assignAllPossibleGroupTaskAssignments,
  } = PlanningActionHandler();

  const SIZE: number = 10;

  const magnifierIcon = (
    <FaMagnifyingGlass
      style={{ width: rem(20), height: rem(20), color: 'gray' }}
    />
  );
  const calendarIcon = (
    <FaRegCalendarAlt
      style={{ width: rem(20), height: rem(20), color: 'blue' }}
    />
  );

  const { data: task } = useSWR<Task>(
    `/client-plans/${props.clientPlanId}/tasks/${props.taskId}`,
    getFetcher
  );

  const { data: genderMap } = useSWR<Mapping>('/mappings/sexes', getFetcher);
  const { data: allTags } = useSWR<Tags>(`/tags`, getFetcher);

  useEffect(() => {
    if (task && allTags) {
      setForm({
        ...form,
        name: task.name,
        note: task.note,
        boundStartTime: task.boundStartTime,
        boundEndTime: task.boundEndTime,
        startTime: task.startTime,
        duration: task.duration,
        date: task.date,
        prefSex: task.prefSex,
        tags: task.tags.map(tag => tag.id),
      });
      setSelectedValues(task.tags.map(tag => tag.name));
    }
  }, [task, allTags]);

  const updateFormField = <K extends keyof TaskRequest>(
    fieldName: K,
    value: TaskRequest[K] | null
  ): void => {
    setForm(prev => {
      return { ...prev, [fieldName]: value };
    });
  };

  const handleChangeTags = (values: string[]): void => {
    setSelectedValues(values);
    const tagIds =
      allTags?.content
        .filter(tag => values.includes(tag.name))
        .map(tag => tag.id) ?? [];
    updateFormField('tags', tagIds);
  };

  const { data: taskGroup } = useSWR<TaskGroup>(
    task?.groupId
      ? `/client-plans/${props.clientPlanId}/task-groups/${task?.groupId}`
      : null,
    getFetcher
  );

  const [assignedEmployees, setAssignedEmployees] = useState<number[]>([]);
  const [groupTasks, setGroupTasks] = useState<GroupTask[]>([]);

  useEffect(() => {
    if (props.opened) {
      reloadAssigned();
    }
  }, [props.opened]);

  useEffect(() => {
    setGroupTasks(
      taskGroup
        ? taskGroup?.tasks.map(task => {
            const d = new Date(task.date);
            const day = d.getDate();
            const month = d.getMonth() + 1;

            const formattedDay = day < 10 ? `0${day}` : day.toString();
            const formattedMonth = month < 10 ? `0${month}` : month.toString();
            const formattedDate = `${formattedDay}.${formattedMonth}.`;

            return {
              taskId: task.id,
              taskDate: formattedDate,
              assign: task.assign
                ? task.assign.map(assignee => {
                    return assignee.id;
                  })
                : [],
            };
          })
        : []
    );
  }, [taskGroup]);

  const createSuggestionsUrl = () => {
    if (!props.opened || !task?.id) return null;

    const baseUrl = '/suggestions';
    const paginationParams = `page=${page}&size=${SIZE}`;
    const occupiedParam = showOccupied ? '&forced=true' : '';
    const searchParam = searchQuery ? `&query=${searchQuery}` : '';
    const taskParam = task.groupId ? `group=${task.groupId}` : `task=${task.id}`;

    return `${baseUrl}?${taskParam}&${paginationParams}${occupiedParam}${searchParam}`;
  };

  const url = createSuggestionsUrl();

  const { data: suggestions } = useSWR<Suggestions>(url, getFetcher);

  const fetchAllAssignedPages = async (): Promise<number[]> => {
    let allAssigned: number[] = [];
    let currentPage = 0;
    let lastPage = false;

    while (!lastPage) {
      const assignedEmployeesReq: TaskAssignments = await getFetcher(
        `/client-plans/${props.clientPlanId}/tasks/${props.taskId}/assignments?page=${currentPage}`
      );
      allAssigned = allAssigned.concat(
        assignedEmployeesReq.content.map(a => a.empId)
      );
      lastPage = assignedEmployeesReq.last;
      currentPage += 1;
    }

    return allAssigned;
  };

  const reloadAssigned = async () => {
    const allAssignedEmployees = await fetchAllAssignedPages();
    setAssignedEmployees(allAssignedEmployees);

    if (task?.groupId) {
      const taskGroup: TaskGroup = await getFetcher(
        task?.groupId
          ? `/client-plans/${props.clientPlanId}/task-groups/${task?.groupId}`
          : ''
      );

      setGroupTasks(
        taskGroup
          ? taskGroup?.tasks.map(task => {
              const d = new Date(task.date);
              const day = d.getDate();
              const month = d.getMonth() + 1;

              const formattedDay = day < 10 ? `0${day}` : day.toString();
              const formattedMonth =
                month < 10 ? `0${month}` : month.toString();
              const formattedDate = `${formattedDay}.${formattedMonth}.`;

              return {
                taskId: task.id,
                taskDate: formattedDate,
                assign: task.assign
                  ? task.assign.map(assignee => {
                      return assignee.id;
                    })
                  : [],
              };
            })
          : []
      );
    }
  };

  const handleAssignButton = async (taskId: number, empId: number) => {
    try {
      await postFetcher(
        `/client-plans/${props.clientPlanId}/tasks/${taskId}/assignments`,
        {
          emps: [empId],
        }
      );
      notifications.show({
        message: 'Pracovník přiřazen',
        color: 'green',
        autoClose: 1000,
        icon: <CheckIcon style={{ width: rem(20), height: rem(20) }} />,
      });
      await reloadAssigned();
      mutate(url);
    } catch (err) {
      displayError(err);
    }
  };

  const handleRemoveAssignButton = async (taskId: number, empId: number) => {
    try {
      await deleteFetcher(
        `/client-plans/${props.clientPlanId}/tasks/${taskId}/assignments?emps=${empId}`
      );
      notifications.show({
        message: 'Pracovník odebrán',
        color: 'green',
        autoClose: 1000,
        icon: <CheckIcon style={{ width: rem(20), height: rem(20) }} />,
      });
      await reloadAssigned();
      mutate(url);
    } catch (err) {
      displayError(err);
    }
  };

  const handleSaveButton = async () => {
    let updatedByMonthDay = taskGroup?.byMonthDay ?? [];

    if (task?.groupId !== null) {
      const originalTaskDate = dayjs(task?.date).date();
      const newTaskDate = dayjs(form.date).date();
      updatedByMonthDay = updatedByMonthDay.filter(
        day => day !== originalTaskDate
      );
      updatedByMonthDay.push(newTaskDate);
    }

    const body = {
      name: form.name,
      note: form.note,
      boundStartTime: form.boundStartTime,
      boundEndTime: form.boundEndTime,
      startTime: form.startTime,
      duration: form.duration,
      date: form.date,
      prefSex: form.prefSex,
      tags: form.tags,
      byMonthDay: task?.groupId !== null ? updatedByMonthDay : undefined,
    };

    try {
      const url =
        task?.groupId !== null
          ? `/client-plans/${props.clientPlanId}/task-groups/${task?.groupId}`
          : `/client-plans/${props.clientPlanId}/tasks/${props.taskId}`;

      await putFetcher(url, body);

      notifications.show({
        message: 'Úkol byl úspěšně aktualizován',
        color: 'green',
        icon: <CheckIcon style={{ width: rem(20), height: rem(20) }} />,
      });
      props.close();
    } catch (err) {
      displayError(err);
    }
  };

  const handleDeleteTask = async () => {
    await deleteTask(task?.clientPlanId, task?.id);
    props.close();
  };
  const handleDeleteTaskGroup = async () => {
    await deleteTaskGroup(task?.clientPlanId, task?.groupId);
    props.close();
  };

  const handleCancelAllEmployeeAssignments = async () => {
    await cancelAllEmployeeGroupAssignments(task?.clientPlanId, task?.groupId);
    await reloadAssigned();
    mutate(url);
  };

  const handleAssignAllPossibleAssignments = async (emps: number[]) => {
    await assignAllPossibleGroupTaskAssignments(
      task?.clientPlanId,
      task?.groupId,
      emps,
      true
    );
    await reloadAssigned();
    mutate(url);
  };

  const getButtons = (empId: number, available: boolean) => {
    return (
      <>
        {assignedEmployees.includes(empId) ? (
          <Button
            onClick={async () => {
              if (task?.id !== undefined) {
                handleRemoveAssignButton(task?.id, empId);
              }
            }}
          >
            Odebrat
          </Button>
        ) : (
          <Button
            variant="outline"
            disabled={!available}
            onClick={async () => {
              if (task?.id !== undefined) {
                handleAssignButton(task?.id, empId);
              }
            }}
          >
            Přiřadit
          </Button>
        )}
        {task?.groupId !== null && (
          <>
            <Button
              variant="outline"
              style={{ marginLeft: '8px' }}
              onClick={async () => {
                if (task?.id !== undefined) {
                  handleAssignAllPossibleAssignments([empId]);
                }
              }}
            >
              Přiřadit dostupné
            </Button>
            <Button
              style={{ marginLeft: '8px' }}
              onClick={async () => {
                if (task?.id !== undefined) {
                  handleCancelAllEmployeeAssignments();
                }
              }}
            >
              Zrušit vše
            </Button>
          </>
        )}
      </>
    );
  };

  const SelectableTable = () => {
    const handleRowClick = (id: number) => {
      setSelectedRow(id);
    };

    return (
      <table style={{ width: '100%' }}>
        <tbody>
          {suggestions?.content?.map(s => (
            <tr
              key={s.emp.empId}
              onClick={() => handleRowClick(s.emp.empId)}
              style={{
                height: '60px',
                backgroundColor:
                  selectedRow === s.emp.empId
                    ? 'rgba(130, 180, 250, 0.25)'
                    : '',
                cursor: 'pointer',
              }}
            >
              <td style={{ padding: '10px' }}>
                <a
                  href={`/employees/${s.emp.empId}/timetable`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {calendarIcon}
                </a>
              </td>
              <td>{s.emp.firstName + ' ' + s.emp.lastName}</td>
              <td
                align="right"
                style={{
                  color: s.emp.currHours > s.emp.optimum ? 'red' : 'black',
                }}
              >
                {s.emp.currHours}/{s.emp.optimum} {s.emp.empType}
              </td>
              <td align="right">
                {getButtons(
                  s.emp.empId,
                  s.tasks.find(t => t.taskId === props.taskId)?.avail ?? true
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  function showDates() {
    return (
      <div
        style={{
          height: '100%',
          maxHeight: `${(suggestions?.content.length ?? 5) * 60}px`,
          overflowY: 'auto',
        }}
      >
        <Text
          fw={700}
          size="sm"
          style={{
            backgroundColor: 'white',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          Opakování úkolu
        </Text>
        <div>
          {groupTasks?.map(d => (
            <Checkbox
              key={d.taskId}
              size="md"
              mt="sm"
              ml="lg"
              labelPosition="right"
              label={d.taskDate}
              disabled={selectedRow === null}
              checked={selectedRow ? d.assign.includes(selectedRow) : false}
              onClick={() => {
                const checked = selectedRow
                  ? d.assign.includes(selectedRow)
                  : false;
                checked
                  ? handleRemoveAssignButton(d.taskId, selectedRow ?? -1)
                  : handleAssignButton(d.taskId, selectedRow ?? -1);
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  function commonElements() {
    return (
      <>
        <Group justify="space-between">
          <TextInput
            mt="sm"
            variant="filled"
            radius="md"
            placeholder="Vyhledat"
            label="Přiřadit pracovníka"
            leftSection={magnifierIcon}
            value={searchQuery}
            onChange={e => setSearchQuery(e.currentTarget.value)}
            style={{
              width: '65%',
            }}
          />

          <Checkbox
            size="sm"
            mt="xl"
            labelPosition="left"
            label="Zobrazit obsazené zaměstnance"
            checked={showOccupied}
            onChange={e => setShowOccupied(e.currentTarget.checked)}
          />
        </Group>
        <Flex mt="md">
          <Box flex="4">{SelectableTable()}</Box>
          {task?.groupId && (
            <Box
              flex="1"
              style={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              {showDates()}
            </Box>
          )}
        </Flex>
      </>
    );
  }

  function taskAssignments() {
    return (
      <div>
        {commonElements()}
        <Center>
          <Pagination
            total={suggestions?.totalPages ?? 1}
            value={page}
            onChange={setPage}
            withEdges
            size="md"
          />
        </Center>
      </div>
    );
  }

  function taskGroupAssignments() {
    return (
      <div>
        {commonElements()}
        <Center>
          <Pagination
            total={suggestions?.totalPages ?? 1}
            value={page}
            onChange={newPage => {
              setPage(newPage);
              setSelectedRow(null);
            }}
            withEdges
            size="md"
          />
        </Center>
      </div>
    );
  }

  const getModalBody = () => {
    return task?.groupId === null ? taskAssignments() : taskGroupAssignments();
  };

  function ShowTaskModal() {
    return (
      <Modal
        opened={props.opened}
        onClose={() => {
          props.close();
          setSelectedRow(null);
          setPage(1);
        }}
        size="70rem"
        title={task?.name + ' ' + dayjs(task?.date).format(dateFormatUI)}
      >
        {getModalBody()}
        <Flex direction="column">
          <Box mb="md">
            <Divider my="sm" />
            <Text size="lg" fw={500}>
              Vlastnosti požadavku
            </Text>
          </Box>
          <Flex>
            <Box flex="1">
              <DateInput
                variant="filled"
                radius="md"
                label="Datum"
                placeholder="datum"
                valueFormat={dateFormatUI}
                value={dayjs(form.date).toDate()}
                onChange={e => {
                  updateFormField('date', dayjs(e).format('YYYY-MM-DD'));
                }}
              />
              <div style={{ marginTop: '0.875rem' }}>
                <LabelWithTooltip
                  text="Začátek nejdříve"
                  tooltip="Čas, od kdy nejdříve lze začít pracovat na úkolu"
                />
                <TimeInput
                  style={{ marginTop: 0 }}
                  value={form.boundStartTime}
                  onChange={boundStartTime => {
                    updateFormField('boundStartTime', boundStartTime);
                  }}
                />
              </div>
              <div style={{ marginTop: '0.875rem' }}>
                <LabelWithTooltip
                  text="Konec nejpozději"
                  tooltip="Čas, do kdy musí být úkol dokončen"
                />
                <TimeInput
                  style={{ marginTop: 0 }}
                  value={form.boundEndTime}
                  onChange={boundEndTime =>
                    updateFormField('boundEndTime', boundEndTime)
                  }
                />
              </div>
              <Group mt="sm" flex="row" align="center">
                <TimeInput
                  style={{ marginTop: 0 }}
                  label="Začátek výkonu"
                  value={form.startTime}
                  onChange={startTime => {
                    updateFormField('startTime', startTime);
                  }}
                />
                <TextInput
                  label="Trvání"
                  placeholder="počet minut"
                  variant="filled"
                  radius="md"
                  style={{ fontSize: '0.875rem' }}
                  value={form.duration.toString() ?? ''}
                  onChange={e => {
                    updateFormField('duration', parseInt(e.target.value) || 0);
                  }}
                />
              </Group>
            </Box>
            <Box flex="1" ml="md">
              <Textarea
                rows={1}
                label="Poznámka"
                placeholder="poznámka"
                mt="sm"
                variant="filled"
                radius="md"
                styles={{
                  input: {
                    backgroundColor: '#FFF1D0',
                  },
                }}
                value={form.note ?? ''}
                onChange={e => {
                  updateFormField(
                    'note',
                    e.target.value === '' ? null : e.target.value
                  );
                }}
              />
              <Select
                variant="filled"
                radius="md"
                label="Preferované pohlaví"
                placeholder="Žena / Muž / Jiné / Nezáleží"
                allowDeselect
                data={MappingToComboBoxItem(genderMap)}
                value={form.prefSex ?? 'O'}
                onChange={value => {
                  updateFormField('prefSex', value ?? 'O');
                }}
              />
              <MultiSelect
                mt="sm"
                variant="filled"
                radius="md"
                placeholder="přidat tagy"
                label="Tagy"
                value={selectedValues}
                data={allTags?.content.map(tag => tag.name) ?? []}
                onChange={handleChangeTags}
              />
            </Box>
          </Flex>
          <Group mt="md" style={{ justifyContent: 'flex-end' }}>
            <Button onClick={handleSaveButton}>Uložit změny</Button>
            <Button onClick={handleDeleteTask} variant="outline">
              Smazat úkol
            </Button>
            {task?.groupId && (
              <Button onClick={handleDeleteTaskGroup} variant="outline">
                Smazat skupinu úkolů
              </Button>
            )}
          </Group>
          <Divider my="sm" />
        </Flex>
      </Modal>
    );
  }

  return <div>{ShowTaskModal()}</div>;
};
